import React, { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useRouter } from 'next/router'
import analytics from '../../../utils/analytics'
import { ANALYTICS_CATEGORIES, INVALID } from '../../../constants/analytics'
import { LOCATION_SEARCH_TYPE } from '../../../types'

type PackagesFormStateType = {
  tripType: string
  partialStay: boolean
  startLocation: LOCATION_SEARCH_TYPE
  endLocation: LOCATION_SEARCH_TYPE
  startDate: string
  endDate: string
  hotelStartDate: string
  hotelEndDate: string
  roomCount: number
  adultCount: number
  childrenCount: number
  childrenAges: number[]
  isRecommendedTrip: boolean
  includeBasicEconomy: boolean
  pickupCarSomewhere: boolean
  pickupCarIncludeAirport: boolean
  rcLocation: LOCATION_SEARCH_TYPE
  rcPickupDate: string
  rcDropoffDate: string
  rcPickupTime: string
  rcDropoffTime: string
}
type FormContainerProps = {
  children: (args: Record<string, unknown>) => JSX.Element
  initialState: PackagesFormStateType
  onSubmit: (data: PackagesFormStateType) => void
  searchFormSchema: Record<string, unknown>
  fields: string[]
  homepage: boolean
}
export default function FormContainer({
  children,
  initialState,
  onSubmit,
  fields,
  searchFormSchema
}: FormContainerProps) {
  const {
    register,
    unregister,
    setValue,
    clearError,
    formState,
    handleSubmit,
    watch,
    errors
  } = useForm<PackagesFormStateType>({
    defaultValues: initialState,
    mode: 'onSubmit',
    validationSchema: searchFormSchema,
    reValidateMode: 'onChange'
  })
  useEffect(() => {
    fields.forEach(name => {
      register({ name })
    })
    return () => {
      fields.forEach(name => {
        unregister(name)
      })
    }
  }, [fields, register, unregister])

  const {
    tripType: watchTripType,
    partialStay: watchPartialStay,
    startLocation: watchStartLocation,
    endLocation: watchEndLocation,
    startDate: watchStartDate,
    endDate: watchEndDate,
    roomCount: watchRoomCount,
    adultCount: watchAdultCount,
    childrenAges: watchChildrenAges,
    childrenCount: watchChildrenCount,
    hotelStartDate: watchHotelStartDate,
    hotelEndDate: watchHotelEndDate,
    includeBasicEconomy: watchIncludeBasicEconomy,
    pickupCarSomewhere: watchPickupCarSomewhere,
    pickupCarIncludeAirport: watchPickupCarIncludeAirport,
    rcLocation: watchRcLocation,
    rcPickupDate: watchRcPickupDate,
    rcDropoffDate: watchRcDropoffDate,
    rcPickupTime: watchRcPickupTime,
    rcDropoffTime: watchRcDropoffTime
  } = watch()

  useEffect(() => {
    clearError('startLocation')
  }, [clearError, watchStartLocation])
  useEffect(() => {
    clearError('endLocation')
  }, [clearError, watchEndLocation])
  useEffect(() => {
    clearError('rcLocation')
  }, [clearError, watchRcLocation])
  useEffect(() => {
    clearError('startDate')
  }, [clearError, watchStartDate])
  useEffect(() => {
    clearError('endDate')
  }, [clearError, watchEndDate])
  useEffect(() => {
    clearError('hotelStartDate')
  }, [clearError, watchHotelStartDate, watchPartialStay])
  useEffect(() => {
    clearError('hotelEndDate')
  }, [clearError, watchHotelEndDate, watchPartialStay])
  useEffect(() => {
    clearError('hotelStartDateInRange')
  }, [clearError, watchPartialStay, watchStartDate, watchHotelStartDate])
  useEffect(() => {
    clearError('hotelEndDateInRange')
  }, [clearError, watchPartialStay, watchEndDate, watchHotelEndDate])
  useEffect(() => {
    clearError('rcPickupDate')
  }, [clearError, watchRcPickupDate])
  useEffect(() => {
    clearError('rcDropoffDate')
  }, [clearError, watchRcDropoffDate])
  useEffect(() => {
    clearError('isInfantOnInternationalTravel')
  }, [clearError, watchStartLocation, watchEndLocation, watchChildrenAges])
  useEffect(() => {
    clearError('isChildrenAgeProvided')
  }, [clearError, watchChildrenCount, watchChildrenAges])
  useEffect(() => {
    clearError('isRoomCompacityReached')
  }, [
    clearError,
    watchAdultCount,
    watchChildrenCount,
    watchTripType,
    watchRoomCount
  ])
  const { pathname } = useRouter()
  const ANALYTICS_CATEGORY = ANALYTICS_CATEGORIES[pathname]

  useEffect(() => {
    setValue('gaCategory', ANALYTICS_CATEGORY)
  }, [])

  useEffect(() => {
    if (formState.submitCount && !formState.isValid) {
      analytics.fireEvent({
        category: ANALYTICS_CATEGORY,
        action: 'VP Search Button',
        label: INVALID
      })
    }
  }, [formState.isValid, formState.submitCount])

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      {children({
        setValue,
        errors,
        register,
        watchState: {
          watchTripType,
          watchAdultCount,
          watchRoomCount,
          watchChildrenAges,
          watchChildrenCount,
          watchHotelStartDate,
          watchHotelEndDate,
          watchStartDate,
          watchEndDate,
          watchPartialStay,
          watchStartLocation,
          watchEndLocation,
          watchIncludeBasicEconomy,
          watchPickupCarSomewhere,
          watchPickupCarIncludeAirport,
          watchRcLocation,
          watchRcPickupDate,
          watchRcDropoffDate,
          watchRcPickupTime,
          watchRcDropoffTime
        }
      })}
    </form>
  )
}
